import { useState } from "react";
import "@/App.css";
import axios from "axios";
import SignalTester from "@/SignalTester";
import Portal from "@/Portal";
import AuditPanel from "@/AuditPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const STEPS = [
  {
    n: "01",
    title: "Send a procurement event",
    body: "POST a transaction to /api/v1/signal — supplier, amount, currency, category, date. One event, one call.",
  },
  {
    n: "02",
    title: "Receive a priority signal",
    body: "Get a single priority score and status (NORMAL → HIGH PRIORITY), fused from five deterministic signals.",
  },
  {
    n: "03",
    title: "Act on the explanation",
    body: "Every signal carries a plain-English reason and evidence reference, so your team knows exactly why it was flagged.",
  },
];

const DEMO_SIGNALS = [
  { name: "transaction_amount", score: 69, status: "REVIEW", text: "This transaction of 150,000 USD is 4.2× the supplier's average of 35,714 USD across 7 prior transactions, exceeding their previous maximum." },
  { name: "spend_change", score: 100, status: "REVIEW", text: "Current-period spend of 155,000 USD is +4900% versus 5,000 USD in the previous 30-day period." },
  { name: "supplier_activity", score: 76, status: "WATCH", text: "Transaction frequency changed +200% — 12 events this period versus 4 in the previous period." },
  { name: "data_quality", score: 0, status: "NORMAL", text: "All optional supplier and transaction fields are present and the amount is plausible." },
  { name: "category_consistency", score: 0, status: "NORMAL", text: "Category \"IT Hardware\" is consistent with this supplier's history." },
];

const CODE_REQUEST = `POST /api/v1/signal
Content-Type: application/json

{
  "event_id": "po-2026-0001",
  "event_type": "purchase_order",
  "supplier_id": "sup-42",
  "supplier_name": "Acme Ltd",
  "category": "IT Hardware",
  "amount": 150000,
  "currency": "USD",
  "transaction_date": "2026-06-15T10:00:00Z",
  "source_system": "coupa",
  "metadata": { "po_number": "PO-9987" }
}`;

const CODE_CURL = `curl -X POST https://your-host/api/v1/signal \\
  -H 'Content-Type: application/json' \\
  -d '{
    "event_id": "po-2026-0001",
    "event_type": "purchase_order",
    "supplier_id": "sup-42",
    "amount": 150000,
    "currency": "USD",
    "transaction_date": "2026-06-15T10:00:00Z",
    "source_system": "coupa"
  }'

# Retrieve it later:
curl https://your-host/api/v1/signal/po-2026-0001`;

const CODE_TS = `type ProcurementEvent = {
  event_id: string; event_type: string; supplier_id: string;
  supplier_name?: string; category?: string; amount: number;
  currency: string; transaction_date: string; source_system: string;
  metadata?: Record<string, unknown>;
};

async function submitEvent(baseUrl: string, event: ProcurementEvent) {
  const res = await fetch(\`\${baseUrl}/api/v1/signal\`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(event),
  });
  if (res.status === 409) {
    return fetch(\`\${baseUrl}/api/v1/signal/\${event.event_id}\`).then(r => r.json());
  }
  if (!res.ok) throw new Error(\`Signal API error \${res.status}\`);
  const signal = await res.json();
  if (["REVIEW", "HIGH_PRIORITY"].includes(signal.priority_status)) {
    // route to a human reviewer using signal.top_contributors[].explanation
  }
  return signal;
}`;

const CODE_RESPONSE = `{
  "event_id": "po-2026-0001",
  "supplier_id": "sup-42",
  "supplier_name": "Acme Ltd",
  "priority_score": 72,
  "priority_status": "REVIEW",
  "top_contributors": [
    { "signal_name": "spend_change", "score": 100,
      "explanation": "Current-period spend is +4900% vs previous period." },
    { "signal_name": "supplier_activity", "score": 76,
      "explanation": "Transaction frequency changed +200%." },
    { "signal_name": "transaction_amount", "score": 69,
      "explanation": "4.2× the supplier's average, exceeding prior max." }
  ],
  "confidence": { "score": 100, "level": "HIGH" },
  "data_quality": { "score": 0, "status": "NORMAL", "completeness_ratio": 1 },
  "methodology_version": { "signal_engine": "2026.06.2", "fusion": "fusion-1.0.0" },
  "timestamp": "2026-06-15T10:00:00.000Z",
  "evidence_reference": {
    "input_hash": "…", "retrieval_url": "/api/v1/signal/po-2026-0001"
  }
}`;

const INTEGRATION_EXAMPLES = [
  { title: "JSON request", code: CODE_REQUEST },
  { title: "curl", code: CODE_CURL },
  { title: "TypeScript", code: CODE_TS },
  { title: "JSON response", code: CODE_RESPONSE },
];

/* ---------------- Partner access (PIN-gated demo) ---------------- */

const usePartnerAccess = () => {
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^\d{6}$/.test(pin.trim())) {
      setErr("Enter the 6-digit access PIN.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/partner-access`, { pin: pin.trim() });
      if (res.data?.valid) setUnlocked(true);
      else setErr("Incorrect PIN. Please check with your Signal Wrapper contact.");
    } catch {
      setErr("Couldn't verify the PIN right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return { pin, setPin, unlocked, loading, err, submit };
};

const PinGate = ({ pin, setPin, loading, err, onSubmit }) => (
  <form className="sw-gate" onSubmit={onSubmit} data-testid="pin-form" noValidate>
    <span className="sw-gate-label" data-testid="partner-access-label">
      Partner Access
    </span>
    <p className="sw-gate-copy">
      Enter your 6-digit demo PIN to unlock the live signal demo and integration examples.
    </p>
    <div className="sw-gate-row">
      <input
        type="text"
        inputMode="numeric"
        maxLength={6}
        className="sw-input sw-pin"
        placeholder="••••••"
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
        data-testid="pin-input"
        aria-label="Partner access PIN"
      />
      <button
        type="submit"
        className="sw-btn sw-btn-primary"
        disabled={loading}
        data-testid="pin-submit"
      >
        {loading ? "Checking…" : "Unlock demo"}
      </button>
    </div>
    {err ? (
      <p className="sw-error" data-testid="pin-error">
        {err}
      </p>
    ) : null}
  </form>
);

const SignalDemoCard = () => (
  <div className="sw-demo-card">
    <div className="sw-sample-head">
      <div>
        <div className="sw-sample-label">Supplier · sup-42</div>
        <div className="sw-sample-name">Acme Ltd</div>
      </div>
      <div className="sw-badge">
        <span className="sw-tick" /> Review
      </div>
    </div>
    <div className="sw-sample-score">
      72<span className="sw-slash">/100</span>
    </div>
    <div className="sw-sample-meter">
      <i style={{ width: "72%" }} />
    </div>
    <div className="sw-demo-signals">
      {DEMO_SIGNALS.map((s) => (
        <div className="sw-demo-signal" key={s.name}>
          <div className="sw-demo-signal-top">
            <span className="sw-demo-signal-name">{s.name.replace(/_/g, " ")}</span>
            <span className={`sw-pill sw-pill-${s.status.toLowerCase()}`}>
              {s.status} · {s.score}
            </span>
          </div>
          <p className="sw-demo-signal-text">{s.text}</p>
        </div>
      ))}
    </div>
  </div>
);

const IntegrationExamples = () => (
  <div className="sw-integration" data-testid="integration-examples">
    {INTEGRATION_EXAMPLES.map((ex) => (
      <div className="sw-code-block" key={ex.title}>
        <span className="sw-code-title">{ex.title}</span>
        <pre className="sw-code">{ex.code}</pre>
      </div>
    ))}
  </div>
);

function PartnerAccess() {
  const { pin, setPin, unlocked, loading, err, submit } = usePartnerAccess();
  return (
    <section className="sw-partner" id="partner" data-testid="partner-access-section">
      {!unlocked ? (
        <PinGate pin={pin} setPin={setPin} loading={loading} err={err} onSubmit={submit} />
      ) : (
        <>
          <div className="sw-section-head">
            <span className="sw-kicker">Partner Access · Unlocked</span>
            <h2 className="sw-h2">Live signal demo & integration.</h2>
          </div>
          <div className="sw-tester-block" data-testid="partner-tester">
            <h3 className="sw-portal-h3">Try it — submit a live event</h3>
            <SignalTester idPrefix="partner-tester" />
          </div>
          <p className="sw-disclaimer" data-testid="signal-disclaimer">
            Signal Wrapper provides deterministic prioritization signals based on available
            transaction data only. Scores are not legal determinations, fraud assessments,
            regulatory compliance certifications, or financial advice. Results should be reviewed
            by qualified personnel before any procurement decision is made. Methodology version is
            recorded with every result for reproducibility and audit purposes.
          </p>
          <div className="sw-demo-grid" data-testid="demo-content">
            <SignalDemoCard />
            <IntegrationExamples />
          </div>
          <AuditPanel />
        </>
      )}
    </section>
  );
}

/* ---------------- Access request form ---------------- */

const useAccessRequest = () => {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [useCase, setUseCase] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!emailValid) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/access-requests`, {
        email: email.trim(),
        company: company.trim() || null,
        use_case: useCase.trim() || null,
      });
      setMessage(res.data?.message || "Access request received.");
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong submitting your request. Please try again."
      );
    }
  };

  const reset = () => {
    setEmail("");
    setCompany("");
    setUseCase("");
    setStatus("idle");
    setMessage("");
    setErrorMsg("");
  };

  return {
    email, setEmail, company, setCompany, useCase, setUseCase,
    status, message, errorMsg, submit, reset,
  };
};

const AccessConfirmation = ({ message, onReset }) => (
  <div className="sw-form-card sw-confirm" data-testid="access-confirmation">
    <div className="sw-confirm-badge">
      <span className="sw-tick" /> Request received
    </div>
    <h3 className="sw-confirm-title">You're on the list.</h3>
    <p className="sw-confirm-body" data-testid="confirmation-message">
      {message}
    </p>
    <button
      type="button"
      className="sw-btn sw-btn-ghost"
      onClick={onReset}
      data-testid="request-another-btn"
    >
      Submit another request
    </button>
  </div>
);

function AccessForm() {
  const f = useAccessRequest();

  if (f.status === "success") {
    return <AccessConfirmation message={f.message} onReset={f.reset} />;
  }

  return (
    <form className="sw-form-card" onSubmit={f.submit} data-testid="access-form" noValidate>
      <label className="sw-field">
        <span className="sw-label">Work email *</span>
        <input
          type="email"
          className="sw-input"
          placeholder="you@company.com"
          value={f.email}
          onChange={(e) => f.setEmail(e.target.value)}
          data-testid="email-input"
          required
        />
      </label>

      <label className="sw-field">
        <span className="sw-label">Company</span>
        <input
          type="text"
          className="sw-input"
          placeholder="Acme Procurement"
          value={f.company}
          onChange={(e) => f.setCompany(e.target.value)}
          data-testid="company-input"
        />
      </label>

      <label className="sw-field">
        <span className="sw-label">What would you use it for?</span>
        <textarea
          className="sw-input sw-textarea"
          placeholder="e.g. Flagging high-risk purchase orders before approval"
          value={f.useCase}
          onChange={(e) => f.setUseCase(e.target.value)}
          data-testid="usecase-input"
          rows={3}
        />
      </label>

      {f.errorMsg ? (
        <p className="sw-error" data-testid="form-error">
          {f.errorMsg}
        </p>
      ) : null}

      <button
        type="submit"
        className="sw-btn sw-btn-primary"
        disabled={f.status === "loading"}
        data-testid="submit-access-request"
      >
        {f.status === "loading" ? "Submitting…" : "Request access"}
      </button>
      <p className="sw-form-note">No spam. We'll email you when your access is ready.</p>
    </form>
  );
}

/* ---------------- Landing sections ---------------- */

const HeroSection = () => (
  <section className="sw-hero">
    <div className="sw-hero-copy">
      <div className="sw-eyebrow">
        <span className="sw-dot" /> Signal Wrapper · Procurement Priority API
      </div>
      <h1 className="sw-hero-title" data-testid="hero-title">
        Turn procurement events into a single priority signal.
      </h1>
      <p className="sw-hero-sub">
        Signal Wrapper is an upstream API for procurement platforms. Send a transaction
        event and get back a deterministic priority score with a plain-English explanation —
        so your team reviews the right suppliers first. No black-box scoring, no dashboards to
        manage.
      </p>
      <div className="sw-hero-actions">
        <button
          type="button"
          className="sw-btn sw-btn-primary"
          onClick={() => scrollTo("access")}
          data-testid="hero-cta"
        >
          Request access
        </button>
        <button
          type="button"
          className="sw-btn sw-btn-ghost"
          onClick={() => scrollTo("how")}
          data-testid="how-it-works-link"
        >
          How it works
        </button>
      </div>
    </div>

    {/* Sample output visual */}
    <aside className="sw-sample" aria-hidden="true">
      <div className="sw-sample-head">
        <div>
          <div className="sw-sample-label">Supplier</div>
          <div className="sw-sample-name">WindNorthernTraders</div>
        </div>
        <div className="sw-badge">
          <span className="sw-tick" /> Review
        </div>
      </div>
      <div className="sw-sample-score">
        72<span className="sw-slash">/100</span>
      </div>
      <div className="sw-sample-meter">
        <i style={{ width: "72%" }} />
      </div>
      <ul className="sw-sample-list">
        <li>
          <span>Spend change</span>
          <b>+4900%</b>
        </li>
        <li>
          <span>Supplier activity</span>
          <b>+200%</b>
        </li>
        <li>
          <span>Transaction amount</span>
          <b>4.2× avg</b>
        </li>
      </ul>
    </aside>
  </section>
);

const StepsSection = () => (
  <section className="sw-how" id="how" data-testid="steps-section">
    <div className="sw-section-head">
      <span className="sw-kicker">How it works</span>
      <h2 className="sw-h2">Three steps, one integration.</h2>
    </div>
    <div className="sw-steps">
      {STEPS.map((s, i) => (
        <article className="sw-step" key={s.n} data-testid={`step-${i + 1}`}>
          <span className="sw-step-num">{s.n}</span>
          <h3 className="sw-step-title">{s.title}</h3>
          <p className="sw-step-body">{s.body}</p>
        </article>
      ))}
    </div>
  </section>
);

const AccessSection = () => (
  <section className="sw-access" id="access">
    <div className="sw-access-copy">
      <span className="sw-kicker">Early access</span>
      <h2 className="sw-h2">Request access.</h2>
      <p className="sw-access-sub">
        Tell us where to send your API keys. We're onboarding procurement teams in batches.
      </p>
    </div>
    <AccessForm />
  </section>
);

const SiteFooter = () => (
  <footer className="sw-footer">
    <span>Signal Wrapper</span>
    <span className="sw-footer-meta">Deterministic · engine 2026.06.2 · fusion 1.0.0</span>
  </footer>
);

function App() {
  if (window.location.pathname.replace(/\/+$/, "") === "/portal") {
    return <Portal />;
  }
  return (
    <div className="sw-landing" data-testid="landing-page">
      <HeroSection />
      <StepsSection />
      <PartnerAccess />
      <AccessSection />
      <SiteFooter />
    </div>
  );
}

export default App;
